import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Lightbulb, Sparkles, Loader2, ArrowLeft, Home, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ParentActivitySuggester() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [topic, setTopic] = useState('');
  const [childAge, setChildAge] = useState('8');
  const [duration, setDuration] = useState('30');
  const [materials, setMaterials] = useState('');
  const [generating, setGenerating] = useState(false);
  const [activities, setActivities] = useState([]);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic or subject');
      return;
    }

    // Check subscription
    if (!user) {
      toast.error('Please sign in to use this tool');
      navigate('/login?redirect=/parent/activity-suggester');
      return;
    }

    // Check if user has parent, plus, or pro subscription
    if (!['parent', 'plus', 'pro'].includes(user.subscription_tier)) {
      toast.error('Please upgrade to access this tool');
      navigate('/parent/pricing');
      return;
    }

    setGenerating(true);
    setActivities([]);

    try {
      const response = await axios.post(
        `${API}/ai/suggest-activities`,
        {
          topic: topic.trim(),
          age: parseInt(childAge),
          duration: parseInt(duration),
          materials: materials.trim() || 'common household items'
        },
        { withCredentials: true }
      );

      setActivities(response.data.activities);
      toast.success('Activities generated!');
    } catch (error) {
      console.error('Error generating activities:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate activities');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
      <Navigation />
      
      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <button
            onClick={() => navigate('/parent')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Parent Portal
          </button>

          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-2xl mb-4 shadow-lg">
              <Lightbulb className="w-8 h-8 text-white" strokeWidth={2} />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              AI Activity Suggester
            </h1>
            <p className="text-base text-gray-600">
              Get fun, educational activities to do at home with your child
            </p>
          </div>

          {/* Input Form */}
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-6">
            <div className="space-y-6">
              {/* Topic Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Topic or Subject
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="E.g., Math, Science, Reading, Geography"
                  className="w-full rounded-xl border-2 border-gray-200 p-4 text-base focus:border-yellow-600 focus:ring-2 focus:ring-yellow-100 outline-none transition-all"
                />
              </div>

              {/* Age and Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Child's Age
                  </label>
                  <select
                    value={childAge}
                    onChange={(e) => setChildAge(e.target.value)}
                    className="w-full rounded-xl border-2 border-gray-200 p-4 text-base focus:border-yellow-600 focus:ring-2 focus:ring-yellow-100 outline-none transition-all"
                  >
                    <option value="5">5 years</option>
                    <option value="6">6 years</option>
                    <option value="7">7 years</option>
                    <option value="8">8 years</option>
                    <option value="9">9 years</option>
                    <option value="10">10 years</option>
                    <option value="11">11 years</option>
                    <option value="12">12 years</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Duration
                  </label>
                  <select
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    className="w-full rounded-xl border-2 border-gray-200 p-4 text-base focus:border-yellow-600 focus:ring-2 focus:ring-yellow-100 outline-none transition-all"
                  >
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">1 hour</option>
                  </select>
                </div>
              </div>

              {/* Materials */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Materials Available (Optional)
                </label>
                <input
                  type="text"
                  value={materials}
                  onChange={(e) => setMaterials(e.target.value)}
                  placeholder="E.g., Paper, markers, household items"
                  className="w-full rounded-xl border-2 border-gray-200 p-4 text-base focus:border-yellow-600 focus:ring-2 focus:ring-yellow-100 outline-none transition-all"
                />
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl px-6 py-4 font-semibold shadow-md hover:shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating Activities...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate Activities
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Activities Result */}
          {activities.length > 0 && (
            <div className="space-y-4">
              {activities.map((activity, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center text-white font-bold shadow-md">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {activity.title}
                      </h3>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                          <Clock className="w-3 h-3" />
                          {activity.duration || duration} min
                        </span>
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                          <Home className="w-3 h-3" />
                          At Home
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-1">Description:</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">{activity.description}</p>
                    </div>

                    {activity.materials && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-1">Materials Needed:</h4>
                        <p className="text-sm text-gray-600">{activity.materials}</p>
                      </div>
                    )}

                    {activity.steps && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-1">Steps:</h4>
                        <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                          {activity.steps.map((step, i) => (
                            <li key={i}>
                              {typeof step === 'string' 
                                ? step 
                                : `${step.instruction || step.description || JSON.stringify(step)}`}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Examples */}
          {activities.length === 0 && !generating && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Example Topics:
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {['Math', 'Science', 'Reading', 'Art', 'Geography', 'History'].map((example) => (
                  <button
                    key={example}
                    onClick={() => setTopic(example)}
                    className="px-4 py-3 rounded-lg bg-yellow-50 hover:bg-yellow-100 transition-colors text-sm font-medium text-gray-700"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ParentActivitySuggester;
